const MAX_MANIFEST_BYTES = 64 * 1024;
const MANIFEST_TIMEOUT_MS = 8000;

function remoteUrl(input, base) {
  let url;
  try {
    url = base ? new URL(input, base) : new URL(input);
  } catch (_error) {
    throw new Error("URL inválida");
  }
  const host = url.hostname.replace(/^\[|\]$/g, "").toLowerCase();
  if (url.protocol !== "https:" || !host || /^\d+\.\d+\.\d+\.\d+$/.test(host) ||
      host.includes(":") || /^\d+$/.test(host) || host === "localhost" ||
      host.endsWith(".localhost") || host.endsWith(".local") ||
      host.endsWith(".internal") || url.username || url.password) {
    throw new Error("La app debe utilizar una URL HTTPS pública sin credenciales");
  }
  return url;
}

export function validateManifestUrl(value) {
  if (typeof value !== "string" || !value.trim() || value.length > 2048) {
    throw new Error("Indica una URL de manifiesto (máximo 2048 caracteres)");
  }
  const url = remoteUrl(value);
  if (url.hash) throw new Error("El manifiesto no debe tener fragmento (#)");
  return url.href;
}

async function readLimitedBody(response, controller) {
  const contentLength = Number(response.headers.get("content-length"));
  if (contentLength > MAX_MANIFEST_BYTES) {
    throw new Error("El manifiesto excede 64 KiB");
  }
  if (!response.body || typeof response.body.getReader !== "function") {
    throw new Error("No se puede leer de forma segura el manifiesto");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let size = 0;
  let content = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > MAX_MANIFEST_BYTES) {
        controller.abort();
        throw new Error("El manifiesto excede 64 KiB");
      }
      content += decoder.decode(value, { stream: true });
    }
    return content + decoder.decode();
  } finally {
    reader.releaseLock();
  }
}

/** No se envían cookies, datos de usuario ni cabeceras de autenticación. */
export async function loadCommandCenterManifest(manifestUrl, options = {}) {
  const url = new URL(validateManifestUrl(manifestUrl));
  const portalOrigin = options.portalOrigin || globalThis.location?.origin || "";
  if (url.origin === portalOrigin) {
    throw new Error("La app debe estar alojada en otro origen que el portal");
  }

  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () => controller.abort();
  if (options.signal) {
    options.signal.addEventListener("abort", onAbort, { once: true });
    if (options.signal.aborted) controller.abort();
  }
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, MANIFEST_TIMEOUT_MS);
  try {
    const fetcher = options.fetcher || globalThis.fetch;
    const response = await fetcher(url.href, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      redirect: "error",
      cache: "no-store",
      referrerPolicy: "no-referrer",
      headers: { Accept: "application/manifest+json, application/json" },
      signal: controller.signal,
    });
    if (!response.ok || response.type === "opaque") {
      throw new Error(`No se pudo descargar el manifiesto (HTTP ${response.status})`);
    }
    if (response.url && new URL(response.url).origin !== url.origin) {
      throw new Error("El manifiesto redirige a otro origen");
    }
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("text/html")) {
      throw new Error("La URL devuelve HTML, no un manifiesto JSON");
    }
    const raw = await readLimitedBody(response, controller);
    let manifest;
    try {
      manifest = JSON.parse(raw);
    } catch (_error) {
      throw new Error("El manifiesto no es un JSON válido");
    }
    if (!manifest || typeof manifest !== "object" || Array.isArray(manifest) ||
        typeof manifest.name !== "string" || !manifest.name.trim() ||
        manifest.name.length > 100 || typeof manifest.start_url !== "string" ||
        !manifest.start_url.trim() || !Array.isArray(manifest.icons) ||
        manifest.icons.length === 0 || manifest.icons.length > 50) {
      throw new Error("El manifiesto requiere name, start_url e icons[].src");
    }
    const start = remoteUrl(manifest.start_url, url);
    if (start.origin !== url.origin || start.origin === portalOrigin) {
      throw new Error("start_url debe compartir el origen HTTPS del manifiesto, no el del portal");
    }
    let icon = null;
    for (const entry of manifest.icons) {
      if (!entry || typeof entry.src !== "string" || !entry.src.trim()) continue;
      try {
        icon = remoteUrl(entry.src, url);
        break;
      } catch (_error) { /* Se intenta el siguiente icono */ }
    }
    if (!icon) throw new Error("El manifiesto no tiene un icono HTTPS válido");
    return {
      name: manifest.name.trim(),
      iconUrl: icon.href,
      startUrl: start.href,
      origin: start.origin,
    };
  } catch (caught) {
    if (timedOut) throw new Error("El manifiesto tardó demasiado en responder");
    if (options.signal?.aborted) throw new Error("Carga cancelada");
    if (caught instanceof TypeError) {
      throw new Error("No se pudo leer el manifiesto: comprueba CORS, TLS y conectividad");
    }
    throw caught;
  } finally {
    clearTimeout(timer);
    if (options.signal) options.signal.removeEventListener("abort", onAbort);
  }
}